import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import UserProfile from './UserProfile';
import {
  getVisibleRoutes, getRouteById, matchRoutePath,
  getSectionIdFromPath, getRouteText, DEFAULT_ROUTE,
} from './PlatformRoute';

// ── i18n ──────────────────────────────────────────────────────────────────────
const T = {
  en: { platformName: 'EmojiSurveys', connectedAs: 'Connected as:', loading: 'Loading user information...', notFound: 'Section not found', collapse: 'Collapse menu', expand: 'Expand menu', dashboard: 'Dashboard' },
  es: { platformName: 'EmojiSurveys', connectedAs: 'Conectado como:', loading: 'Cargando información del usuario...', notFound: 'Sección no encontrada', collapse: 'Contraer menú', expand: 'Expandir menú', dashboard: 'Dashboard' },
};

// ── Constantes de diseño ──────────────────────────────────────────────────────
const COLORS = {
  sidebarGradientStart:    '#4f46e5',
  sidebarGradientEnd:      '#4338ca',
  sidebarText:             '#fff',
  sidebarActiveBackground: 'rgba(255,255,255,0.15)',
  sidebarHoverBackground:  'rgba(255,255,255,0.1)',
  sidebarBorder:           'rgba(255,255,255,0.2)',
  sidebarActiveBorder:     '#fff',
  headerBackground:        '#fff',
  headerText:              '#374151',
  contentBackground:       '#f5f5f5',
  cardBackground:          '#fff',
};

const DIMENSIONS = {
  sidebarOpenWidth:   '240px',
  sidebarClosedWidth: '60px',
  headerHeight:       '64px',
  contentMaxWidth:    '1200px',
};

// ── URL helpers ───────────────────────────────────────────────────────────────
const getFullSubPathFromURL = () => {
  const parts = window.location.pathname.split('/').filter(Boolean);
  return parts[0] === 'platform' && parts.length > 1 ? parts.slice(1).join('/') : '';
};

const setSectionToURL = (section) =>
  window.history.pushState(null, '', `/platform/${section}`);

// ── Estilos ───────────────────────────────────────────────────────────────────
const menuItemBase = (isOpen, isActive) => ({
  position: 'relative', display: 'flex', alignItems: 'center',
  justifyContent: isOpen ? 'flex-start' : 'center',
  padding: isOpen ? '14px 20px' : '14px 0',
  color: COLORS.sidebarText, cursor: 'pointer', transition: 'all 0.2s',
  whiteSpace: 'nowrap', overflow: 'hidden',
  background: isActive ? COLORS.sidebarActiveBackground : 'transparent',
  borderLeft: `4px solid ${isActive ? COLORS.sidebarActiveBorder : 'transparent'}`,
  fontWeight: isActive ? '600' : '400',
});

const STYLES = {
  tooltip: {
    position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(0,0,0,0.8)', color: '#fff', padding: '6px 10px',
    borderRadius: '4px', fontSize: '14px', whiteSpace: 'nowrap',
    zIndex: 101, marginLeft: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },
  sidebar: (isOpen) => ({
    width: isOpen ? DIMENSIONS.sidebarOpenWidth : DIMENSIONS.sidebarClosedWidth,
    background: `linear-gradient(180deg, ${COLORS.sidebarGradientStart} 0%, ${COLORS.sidebarGradientEnd} 100%)`,
    transition: 'width 0.3s ease', boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
    display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 100, overflow: 'hidden',
  }),
  header: {
    background: COLORS.headerBackground, padding: '0 24px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between',
    minHeight: DIMENSIONS.headerHeight, zIndex: 50,
  },
  main: { flex: 1, padding: '10px', background: COLORS.contentBackground, overflowY: 'auto' },
  contentCard: {
    background: COLORS.cardBackground, borderRadius: '8px', padding: '5px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    maxWidth: DIMENSIONS.contentMaxWidth, margin: '0 auto',
  },
};

// ── Subcomponentes ────────────────────────────────────────────────────────────
const MenuItem = React.memo(({ item, isOpen, isActive, onClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isHovered,   setIsHovered]   = useState(false);

  const handleMouseEnter = useCallback(() => { setIsHovered(true);  setShowTooltip(true);  }, []);
  const handleMouseLeave = useCallback(() => { setIsHovered(false); setShowTooltip(false); }, []);

  const itemStyle = useMemo(() => ({
    ...menuItemBase(isOpen, isActive),
    background: isHovered && !isActive
      ? COLORS.sidebarHoverBackground
      : menuItemBase(isOpen, isActive).background,
  }), [isOpen, isActive, isHovered]);

  return (
    <div
      style={itemStyle} onClick={onClick}
      onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
      role="button" tabIndex={0} aria-label={item.text}
      aria-current={isActive ? 'page' : undefined}
    >
      <span style={{ fontSize: '20px', minWidth: '20px' }}>{item.icon}</span>
      {isOpen && <span style={{ marginLeft: '15px', fontSize: '14px' }}>{item.text}</span>}
      {!isOpen && showTooltip && <div style={STYLES.tooltip}>{item.text}</div>}
    </div>
  );
});
MenuItem.displayName = 'MenuItem';

const ToggleButton = React.memo(({ isPinned, onClick, t }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      style={{
        width: '36px', height: '36px', borderRadius: '8px', border: 'none',
        background: isHovered ? 'rgba(255,255,255,0.3)' : COLORS.sidebarHoverBackground,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '20px', transition: 'all 0.2s', color: COLORS.sidebarText,
        flexShrink: 0, transform: isHovered ? 'scale(1.05)' : 'scale(1)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={isPinned ? t.collapse : t.expand}
      title={isPinned ? t.collapse : t.expand}
    >
      {isPinned ? '◀' : '☰'}
    </button>
  );
});
ToggleButton.displayName = 'ToggleButton';

const SidebarHeader = React.memo(({ isOpen, isPinned, onToggle, t }) => (
  <div style={{
    padding: '15px', color: COLORS.sidebarText,
    borderBottom: `1px solid ${COLORS.sidebarBorder}`,
    minHeight: DIMENSIONS.headerHeight, display: 'flex', alignItems: 'center',
    justifyContent: isOpen ? 'space-between' : 'center', gap: '12px',
  }}>
    {isOpen && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, overflow: 'hidden' }}>
        <div style={{ fontSize: '20px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {t.platformName}
        </div>
      </div>
    )}
    <ToggleButton isPinned={isPinned} onClick={onToggle} t={t} />
  </div>
));
SidebarHeader.displayName = 'SidebarHeader';

const SidebarUserInfo = React.memo(({ currentUser, isOpen, t }) => {
  if (!isOpen) return null;
  return (
    <div style={{ padding: '16px', borderTop: `1px solid ${COLORS.sidebarBorder}`, color: COLORS.sidebarText }}>
      <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>{t.connectedAs}</div>
      <div style={{ fontSize: '14px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {currentUser.displayName || currentUser.email}
      </div>
    </div>
  );
});
SidebarUserInfo.displayName = 'SidebarUserInfo';

const LoadingScreen = ({ t }) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Arial, sans-serif' }}>
    <p>{t.loading}</p>
  </div>
);

// ── Componente principal ──────────────────────────────────────────────────────
const Platform = ({ subPath, onNavigate }) => {
  const { currentUser, userData, logout, language } = useAuth();
  const t    = language === 'es' ? T.es : T.en;
  const lang = language === 'es' ? 'es' : 'en';

  const [isPinned,   setIsPinned]   = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const isOpen = isPinned || isHovering;

  const [activeSection, setActiveSection] = useState(() =>
    getSectionIdFromPath(getFullSubPathFromURL())
  );

  const toggleMenu     = useCallback(() => setIsPinned((v) => !v), []);
  const handleMouseEnter = useCallback(() => setIsHovering(true),  []);
  const handleMouseLeave = useCallback(() => setIsHovering(false), []);

  const handleSectionChange = useCallback((sectionId, params = '') => {
    const fullPath = params ? `${sectionId}${params}` : sectionId;
    setActiveSection(sectionId);
    if (onNavigate) {
      onNavigate(`platform/${fullPath}`);
    } else {
      setSectionToURL(fullPath);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, [onNavigate]);

  // Sincronizar activeSection con la prop subPath (viene del router padre)
  useEffect(() => {
    if (subPath) {
      const sectionId = getSectionIdFromPath(subPath);
      if (sectionId && sectionId !== activeSection) setActiveSection(sectionId);
    }
  }, [subPath, activeSection]);

  // Sincronizar con botones atrás/adelante
  useEffect(() => {
    const onPopState = () => setActiveSection(getSectionIdFromPath(getFullSubPathFromURL()));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const menuItems = useMemo(() =>
    getVisibleRoutes().map((r) => ({ ...r, text: getRouteText(r, lang) })),
  [lang]);

  // FIX: usar la prop `subPath` en lugar de leer window.location dentro del memo
  const currentRoute = useMemo(() => {
    if (subPath) {
      const matched = matchRoutePath(subPath);
      if (matched) return matched;
    }
    return getRouteById(activeSection);
  }, [subPath, activeSection]);

  if (!currentUser) return <LoadingScreen t={t} />;

  // Inline: renderContent no se pasa a hijos memoizados, useCallback no aportaba
  const Component = currentRoute?.component;
  const content = currentRoute
    ? <Component currentUser={currentUser} onNavigate={onNavigate} />
    : <div>{t.notFound}</div>;

  return (
    <div style={{ display: 'flex', height: '100vh', margin: 0, fontFamily: 'Arial, sans-serif', background: COLORS.contentBackground }}>
      <aside style={STYLES.sidebar(isOpen)} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <SidebarHeader isOpen={isOpen} isPinned={isPinned} onToggle={toggleMenu} t={t} />
        <nav style={{ flex: 1, padding: '20px 0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {menuItems.map((item) => (
            <MenuItem
              key={item.id} item={item} isOpen={isOpen}
              isActive={activeSection === item.id}
              onClick={() => handleSectionChange(item.id)}
            />
          ))}
        </nav>
        <SidebarUserInfo currentUser={currentUser} isOpen={isOpen} t={t} />
      </aside>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header style={STYLES.header}>
          <h1 style={{ margin: 0, fontSize: '20px', color: COLORS.headerText, fontWeight: '600' }}>
            {getRouteText(currentRoute, lang) || t.dashboard}
          </h1>
          <UserProfile currentUser={currentUser} userData={userData} onLogout={logout} />
        </header>
        <main style={STYLES.main}>
          <div style={STYLES.contentCard}>{content}</div>
        </main>
      </div>
    </div>
  );
};

export default Platform;